import { DeleteOutlined, PlusOutlined } from '@ant-design/icons'
import { Alert, Button, Empty, Form, Input, List, Modal, Popconfirm, Skeleton, Tabs, Tag, message } from 'antd'
import { useCallback, useEffect, useState } from 'react'
import {
  createSiteTaxonomy,
  createTaxonomyTerm,
  deleteTaxonomyTerm,
  listSiteTaxonomies,
  listTaxonomyTerms,
} from '../../api'
import type { SaasApiError } from '../../api'
import type { Taxonomy, TaxonomyTerm } from '../../types'

/**
 * LiangCollection Full Website Absorption — "条目分类" / "主题分类管理".
 * A generic Taxonomy manager: LiangCollection's preset ships two
 * Taxonomies out of the box ("category" / "topic") and this tab lets the
 * tenant add/rename/delete Terms in each, exactly the capability the
 * legacy admin's Category manager provided — just backed by the new
 * generic Taxonomy/TaxonomyTerm core instead of a museum-only table.
 */
function TermsPanel({ workspaceId, siteId, taxonomy }: { workspaceId: string; siteId: string; taxonomy: Taxonomy }) {
  const [terms, setTerms] = useState<TaxonomyTerm[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [createOpen, setCreateOpen] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [form] = Form.useForm<{ name: string; slug: string; name_secondary?: string; description?: string }>()

  const load = useCallback(() => {
    setError(null)
    listTaxonomyTerms(workspaceId, siteId, taxonomy.id)
      .then((r) => setTerms(r.data))
      .catch((err) => setError((err as SaasApiError).message))
  }, [workspaceId, siteId, taxonomy.id])

  useEffect(load, [load])

  const slugify = (value: string) =>
    value.trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '')

  const onCreate = async () => {
    const values = await form.validateFields()
    setSubmitting(true)
    try {
      await createTaxonomyTerm(workspaceId, siteId, taxonomy.id, {
        slug: values.slug || slugify(values.name),
        name: values.name,
        name_secondary: values.name_secondary || null,
        description: values.description || null,
      })
      message.success(`已添加「${values.name}」`)
      setCreateOpen(false)
      form.resetFields()
      load()
    } catch (err) {
      message.error((err as SaasApiError).message)
    } finally {
      setSubmitting(false)
    }
  }

  const onDelete = async (term: TaxonomyTerm) => {
    try {
      await deleteTaxonomyTerm(workspaceId, siteId, taxonomy.id, term.id)
      message.success(`已删除「${term.name}」`)
      load()
    } catch (err) {
      message.error((err as SaasApiError).message)
    }
  }

  if (error) return <Alert type="error" message={error} showIcon style={{ marginTop: 16 }} />
  if (terms === null) return <Skeleton active paragraph={{ rows: 3 }} style={{ marginTop: 16 }} />

  return (
    <div style={{ marginTop: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreateOpen(true)}>
          新建
        </Button>
      </div>
      {terms.length === 0 ? (
        <Empty description="暂无数据" />
      ) : (
        <List
          bordered
          dataSource={terms}
          rowKey="id"
          renderItem={(term) => (
            <List.Item
              actions={[
                <Popconfirm key="del" title={`确认删除「${term.name}」？`} onConfirm={() => onDelete(term)}>
                  <Button danger type="text" icon={<DeleteOutlined />} title="删除" />
                </Popconfirm>,
              ]}
            >
              <List.Item.Meta
                title={
                  <>
                    {term.name} {term.name_secondary ? <span style={{ color: '#999' }}>({term.name_secondary})</span> : null}
                  </>
                }
                description={
                  <>
                    <Tag>{term.slug}</Tag>
                    <Tag color="blue">{term.entry_count} 项条目</Tag>
                    {term.description ? <span style={{ marginLeft: 8, color: '#999' }}>{term.description}</span> : null}
                  </>
                }
              />
            </List.Item>
          )}
        />
      )}
      <Modal
        title="新建"
        open={createOpen}
        onOk={onCreate}
        onCancel={() => setCreateOpen(false)}
        okText="确定"
        cancelText="取消"
        confirmLoading={submitting}
      >
        <Form form={form} layout="vertical">
          <Form.Item name="name" label="名称" rules={[{ required: true, message: '请输入名称' }]}>
            <Input placeholder="瓷器" />
          </Form.Item>
          <Form.Item name="slug" label="内部 slug" tooltip="URL 标识，留空则根据名称自动生成">
            <Input placeholder="留空自动生成" />
          </Form.Item>
          <Form.Item name="name_secondary" label="英文名称">
            <Input placeholder="Ceramics" />
          </Form.Item>
          <Form.Item name="description" label="描述">
            <Input.TextArea rows={2} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  )
}

/**
 * LiangCollection Website IA restructure (spec §1/§3) — Legacy's own
 * sidebar keeps "条目分类" (Category) and "主题分类管理" (Topic) as two
 * fully independent nav entries/pages (see
 * `G:\LiangCollection\backend\templates\admin_base.html`'s two separate
 * `nav-item`s under 基础功能), never a single merged "Categories & Topics"
 * screen — this single-taxonomy view is what `CategoriesTab`/`TopicsTab`
 * (their own routed pages) now render, one Taxonomy key at a time.
 */
export function SingleTaxonomyPanel({
  workspaceId,
  siteId,
  taxonomyKey,
  taxonomyName,
}: {
  workspaceId: string
  siteId: string
  taxonomyKey: string
  taxonomyName: string
}) {
  const [taxonomy, setTaxonomy] = useState<Taxonomy | null | undefined>(undefined)
  const [error, setError] = useState<string | null>(null)
  const [creating, setCreating] = useState(false)

  const load = useCallback(() => {
    setError(null)
    listSiteTaxonomies(workspaceId, siteId)
      .then((r) => setTaxonomy(r.data.find((t) => t.key === taxonomyKey) ?? null))
      .catch((err) => setError((err as SaasApiError).message))
  }, [workspaceId, siteId, taxonomyKey])

  useEffect(load, [load])

  const ensureTaxonomy = async () => {
    setCreating(true)
    try {
      await createSiteTaxonomy(workspaceId, siteId, { key: taxonomyKey, name: taxonomyName })
      load()
    } catch (err) {
      message.error((err as SaasApiError).message)
    } finally {
      setCreating(false)
    }
  }

  if (error) return <Alert type="error" message={error} showIcon style={{ marginTop: 16 }} />
  if (taxonomy === undefined) return <Skeleton active paragraph={{ rows: 4 }} style={{ marginTop: 16 }} />

  if (taxonomy === null) {
    return (
      <Empty description={`尚未创建"${taxonomyName}"分类体系`} style={{ marginTop: 60 }}>
        <Button type="primary" loading={creating} onClick={ensureTaxonomy}>
          创建{taxonomyName}
        </Button>
      </Empty>
    )
  }

  return <TermsPanel workspaceId={workspaceId} siteId={siteId} taxonomy={taxonomy} />
}

/** @deprecated kept only for the (hidden) legacy-compat route — the real
 * Website nav now routes to `CategoriesTab`/`TopicsTab` directly, one
 * Taxonomy per page, matching Legacy's real IA (see module docstring). */
export default function TaxonomiesTab({ workspaceId, siteId }: { workspaceId: string; siteId: string }) {
  const [taxonomies, setTaxonomies] = useState<Taxonomy[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [creating, setCreating] = useState(false)

  const load = useCallback(() => {
    setError(null)
    listSiteTaxonomies(workspaceId, siteId)
      .then((r) => setTaxonomies(r.data))
      .catch((err) => setError((err as SaasApiError).message))
  }, [workspaceId, siteId])

  useEffect(load, [load])

  const ensureDefaults = async () => {
    setCreating(true)
    try {
      await createSiteTaxonomy(workspaceId, siteId, { key: 'category', name: '条目分类' })
      await createSiteTaxonomy(workspaceId, siteId, { key: 'topic', name: '主题分类' })
      load()
    } catch (err) {
      message.error((err as SaasApiError).message)
    } finally {
      setCreating(false)
    }
  }

  if (error) return <Alert type="error" message={error} showIcon style={{ marginTop: 16 }} />
  if (taxonomies === null) return <Skeleton active paragraph={{ rows: 4 }} style={{ marginTop: 16 }} />

  if (taxonomies.length === 0) {
    return (
      <Empty description="暂无分类体系 — LiangCollection 预设提供条目分类与主题分类" style={{ marginTop: 60 }}>
        <Button type="primary" loading={creating} onClick={ensureDefaults}>
          创建条目分类 + 主题分类
        </Button>
      </Empty>
    )
  }

  return (
    <div style={{ marginTop: 16 }}>
      <Tabs
        items={taxonomies.map((t) => ({
          key: t.id,
          label: t.name,
          children: <TermsPanel workspaceId={workspaceId} siteId={siteId} taxonomy={t} />,
        }))}
      />
    </div>
  )
}
